import React, { memo, Fragment, useEffect, useCallback } from 'react';
import { AccordionBlockForm, AccordionForm } from './AccordionBlock';
import { Row, Col, Label } from 'reactstrap';
import { useObservable } from '@core/utils/hooks/rxjs';
import { SortableElement, SortableHandle, SortEnd } from 'react-sortable-hoc';
import { R } from '@core/utils/r';
import { SortableList } from '@modules/common/Sortable';
import { FormHTML } from '@core/components/form/FormHTML';
import { FormHTMLInline, FormInput } from '@core/components/form';

export interface AccordionBlockSettingsProps {
  form: AccordionBlockForm;
}

export const AccordionBlockSettings = memo((props: AccordionBlockSettingsProps) => {
  const { form } = props;
  const accordions = useObservable(form.controls.accordion.items$, []);
  const accordionValues = useObservable(form.controls.accordion.value$, []);

  useEffect(() => {
    if (accordions.length === 0) {
      form.controls.accordion.push();
    }
  }, [form, accordions]);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      const oldIndex = sort.oldIndex;
      const newIndex = sort.newIndex;
      const newAccordions = R.move(accordionValues, oldIndex, newIndex);
      form.patchValue({
        accordion: newAccordions,
      });
    },
    [form, accordionValues],
  );

  return (
    <div>
      <Fragment>
        {!!accordions.length && <Label>Accordions</Label>}
        <SortableList lockAxis="y" distance={2} onSortEnd={sort => onSortEnd(sort)} useDragHandle={true} helperClass="z5000">
          {accordions.map((ctrl, index) => {
            return <SortableAccordionItem key={index} index={index} ctrl={ctrl} form={form} i={index} />;
          })}
        </SortableList>
      </Fragment>
    </div>
  );
});

interface AccordionItemProps {
  i: number;
  ctrl: AccordionForm;
  form: AccordionBlockForm;
  index: number;
}

const AccordionItem = memo((props: AccordionItemProps) => {
  const { ctrl, form, i, index } = props;

  const DragHandle = SortableHandle(() => <i className="fas fa-grip-vertical" style={{ cursor: 'move' }}></i>);

  return (
    <div>
      <label className="text-muted" style={{ opacity: 0.5 }}>
        Accordion #{i + 1}
      </label>
      <Row key={`question-${i} mb-2 mt-2`}>
        <Col md={1}>
          <DragHandle />
        </Col>
        <Col md={8}>
          <FormInput control={ctrl.controls.heading} />
          <FormInput control={ctrl.controls.paragraph} type="textarea" />
        </Col>
        <Col md={2}>
          {!i && (
            <a className="btn btn-outline-primary btn-sm mr-2" onClick={() => form.controls.accordion.push()}>
              Add Accordion
            </a>
          )}
          {!!i && (
            <a className="btn btn-outline-danger btn-sm mr-2" onClick={() => form.controls.accordion.removeAt(i)}>
              <i className="bx bx-x"></i>
            </a>
          )}
        </Col>
      </Row>
      <hr />
    </div>
  );
});

export const SortableAccordionItem = SortableElement(AccordionItem);
